import { useMediaQuery } from '@repo/ui/hooks/use-media-query'
import { useQuery } from '@tanstack/react-query'
import {
	createFileRoute,
	type ErrorComponentProps,
	Link,
	useRouter
} from '@tanstack/react-router'
import { type ReactNode, useState } from 'react'
import { useTranslation } from '@/lib/i18n'
import { ApiKeysCard } from './-components/api-keys-card'
import {
	DashboardQuotaMetric,
	DashboardSummaryMetrics
} from './-components/dashboard-metrics'
import { DashboardRecentLogs } from './-components/dashboard-recent-logs'
import { DashboardRequestVolume } from './-components/dashboard-request-volume'
import { dashboardUsageQueryOptions } from './-lib/dashboard-queries'

export const Route = createFileRoute('/(private)/dashboard')({
	ssr: false,
	component: DashboardPage,
	errorComponent: DashboardRouteError
})

function DashboardPage() {
	const isDesktop = useMediaQuery({ breakpoint: 'md' })
	const [message, setMessage] = useState<string | null>(null)
	const panels = {
		summary: <DashboardSummaryMetrics />,
		quota: <DashboardQuotaMetric />,
		apiKeys: <ApiKeysCard onMessage={setMessage} />,
		requestVolume: <DashboardRequestVolume />,
		recentLogs: <DashboardRecentLogs />
	}

	return (
		<>
			<main className='portal-container dashboard-main'>
				<DashboardTitle showLogsLink={isDesktop} />
				<DashboardContent panels={panels} />
			</main>
			{message ? (
				<output className='portal-toast'>{message}</output>
			) : null}
		</>
	)
}

function DashboardTitle(props: { showLogsLink: boolean }) {
	const { t } = useTranslation()
	const usageQuery = useQuery(dashboardUsageQueryOptions())

	return (
		<div className='dashboard-title'>
			<div>
				<div className='flex flex-wrap items-center gap-3'>
					<h1>{t('common:dashboard.title')}</h1>
					{usageQuery.isSuccess ? (
						<span className='portal-eyebrow my-0'>
							{usageQuery.data.planCode}
						</span>
					) : null}
				</div>
				<p>{t('common:dashboard.subtitle')}</p>
			</div>
			{props.showLogsLink ? (
				<Link
					className='portal-button portal-button--secondary'
					to='/logs'
				>
					{t('common:dashboard.view_logs')}
				</Link>
			) : null}
		</div>
	)
}

function DashboardContent(props: {
	panels: {
		summary: ReactNode
		quota: ReactNode
		apiKeys: ReactNode
		requestVolume: ReactNode
		recentLogs: ReactNode
	}
}) {
	return (
		<>
			<div className='grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4'>
				{props.panels.summary}
				{props.panels.quota}
			</div>
			<div className='mt-5 grid grid-cols-1 gap-5 md:grid-cols-2'>
				{props.panels.requestVolume}
				{props.panels.recentLogs}
			</div>
			<div className='mt-5 w-full'>{props.panels.apiKeys}</div>
		</>
	)
}

function DashboardRouteError(props: ErrorComponentProps) {
	const router = useRouter()
	const { t } = useTranslation()

	return (
		<main className='portal-container dashboard-main'>
			<section className='portal-card dashboard-route-error' role='alert'>
				<h1>{t('common:dashboard.route_error_title')}</h1>
				<p>{t('common:dashboard.route_error_description')}</p>
				<button
					className='portal-button portal-button--secondary'
					onClick={async () => {
						props.reset()
						await router.invalidate()
					}}
					type='button'
				>
					{t('common:dashboard.retry')}
				</button>
			</section>
		</main>
	)
}
